import { useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useRaidPlanTopExecutionKills } from 'app/frontend/base/config/queries'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import Card from 'app/frontend/base/components/Card'
import LinkButton from '@/components/ui/link-button'
import { imageUrl } from 'app/frontend/base/helpers'
import { RaidPlanCast } from 'app/frontend/types/custom/RaidPlan'
import { formatTimelineTimestamp } from './timelineMath'
import SpellIcon from './SpellIcon'
import { NOTE_ICON_URL } from '../config/generalAbilities'
import {
  cloneActorCasts,
  formatFightDuration,
  getTopExecutionKillTimeline,
} from './warcraftLogsImport'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  owner: RaidPlanOwner
  encounterId: number
  classId: number
  specId: number | null
  onImportCasts: (casts: RaidPlanCast[]) => void
}

type MatchingActorRow = {
  key: string
  durationMs: number
  reportCode: string
  fightId: number
  casts: RaidPlanCast[]
}

const ImportActorFromWarcraftLogsDialog = ({
  open,
  onOpenChange,
  owner,
  encounterId,
  classId,
  specId,
  onImportCasts,
}: Props) => {
  const { t } = useTranslation()
  const { data: kills = [], isLoading } = useRaidPlanTopExecutionKills(
    owner,
    encounterId,
    open,
  )
  const rows = useMemo<MatchingActorRow[]>(() => {
    const classMatchingRows = kills.flatMap((kill) => {
      const timeline = getTopExecutionKillTimeline(kill)
      return timeline.actors
        .map((actor, actorIndex) => {
          if (actor.class_id !== classId) return null
          return {
            key: `${kill.id}-${actor.uuid}-${actorIndex}`,
            durationMs: kill.fightDurationMs,
            reportCode: kill.reportCode,
            fightId: kill.fightId,
            casts: actor.casts,
            actorSpecId: actor.spec_id ?? null,
          }
        })
        .filter(
          (entry): entry is MatchingActorRow & { actorSpecId: number | null } =>
            Boolean(entry),
        )
    })
    const specMatchingRows = classMatchingRows
      .filter((row) => row.actorSpecId === specId)
      .map(({ actorSpecId: _actorSpecId, ...row }) => row)
    if (specMatchingRows.length > 0) return specMatchingRows
    return classMatchingRows
      .filter((row) => row.actorSpecId === null)
      .map(({ actorSpecId: _actorSpecId, ...row }) => row)
  }, [classId, kills, specId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-4xl'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <img src={imageUrl('wcl-logo.png')} className='w-6 h-6' />
            {t('dashboard.team.plans.onboarding.import-from-warcraft-logs')}
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
          </div>
        ) : rows.length === 0 ? (
          <div className='text-sm text-muted-foreground py-4'>
            No characters found in the top 50 guild execution kills for this
            specialization.
          </div>
        ) : (
          <>
            <p>
              Select a character from the top 50 guild execution kills to import
              casts from.
            </p>
            <Card className='max-h-[70vh] overflow-auto'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('dashboard.team.plans.onboarding.wcl.duration')}
                    </TableHead>
                    <TableHead>Casts</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell>
                        {formatFightDuration(row.durationMs)}
                      </TableCell>
                      <TableCell>
                        <div className='flex flex-wrap items-center gap-2 py-0.5'>
                          {row.casts
                            .slice()
                            .sort((left, right) => left.at - right.at)
                            .map((cast) => (
                              <Card
                                key={cast.id}
                                className='inline-flex items-center gap-1.5 px-1.5 py-1'
                              >
                                {cast.spell_id === null ? (
                                  <img
                                    src={NOTE_ICON_URL}
                                    className='h-4 w-4 rounded border border-border'
                                  />
                                ) : (
                                  <a
                                    href={`https://wowhead.com/spell=${cast.spell_id}`}
                                    target='_blank'
                                    rel='noreferrer'
                                  >
                                    <SpellIcon
                                      spellId={cast.spell_id}
                                      className='h-4 w-4 rounded border border-border'
                                    />
                                  </a>
                                )}
                                <span className='text-xs text-muted-foreground'>
                                  {formatTimelineTimestamp(cast.at)}
                                </span>
                              </Card>
                            ))}
                        </div>
                      </TableCell>
                      <TableCell className='text-right'>
                        <div className='flex items-center justify-end gap-2'>
                          <LinkButton
                            size='xs'
                            target='_blank'
                            rel='noreferrer'
                            to={`https://www.warcraftlogs.com/reports/${row.reportCode}#fight=${row.fightId}`}
                          >
                            View
                          </LinkButton>
                          <Button
                            size='xs'
                            onClick={() => {
                              onImportCasts(cloneActorCasts(row.casts))
                              onOpenChange(false)
                            }}
                          >
                            Import
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ImportActorFromWarcraftLogsDialog
